import {
  SCHOLARSHIP_CLASSES,
  SCHOLARSHIP_JUCO_CLASSES,
  type ScholarshipClass,
  type ScholarshipGraduationYear,
  type ScholarshipJucoClass,
  type ScholarshipPlayer,
  type ScholarshipStoredClass,
} from '../../../../types/scholarships';

/*
  One field, two kinds of value. A scholarship roster counts years of eligibility (1-5); a
  recruiting board counts graduation years (2027), because that is how a coach thinks about
  who they are chasing. Which kind a value is, is readable from its shape, so nothing has to
  carry a second field saying which.

  Keep the year shape in sync with ScholarshipPlayerClasses on the API and with
  ck_scholarship_players_class. The century is pinned so a fat-fingered 1027 is not read as a
  class year.
*/
const GRADUATION_YEAR_PATTERN = /^20\d{2}$/;

export const isGraduationYearClass = (v: string): v is ScholarshipGraduationYear =>
  GRADUATION_YEAR_PATTERN.test(v);

export const isEligibilityYearClass = (v: string): boolean =>
  (SCHOLARSHIP_CLASSES as readonly string[]).includes(v);

/*
  A junior college year, derived by the API from a JUCO player's JC year. Display and filter
  only: the class editor never offers one and the API refuses it on a write.
*/
export const isJucoClass = (v: string): v is ScholarshipJucoClass =>
  (SCHOLARSHIP_JUCO_CLASSES as readonly string[]).includes(v);

/** A class a row can store: a year of eligibility or a graduation year. Never a JC class. */
export const isStoredScholarshipClass = (v: string): v is ScholarshipStoredClass =>
  isEligibilityYearClass(v) || isGraduationYearClass(v);

/** Any class a player can be filed under, including a derived JC class. */
export const isScholarshipClass = (v: string): v is ScholarshipClass =>
  isStoredScholarshipClass(v) || isJucoClass(v);

/*
  How many recruiting classes the dropdown offers, starting from the current calendar year.
  A rolling window rather than a fixed list so it never needs revisiting, and it starts at the
  current year rather than the next one so a class still being recruited mid-cycle does not
  drop off the list under a coach who is working on it.
*/
const GRADUATION_YEARS_OFFERED = 6;

export const graduationYearOptions = (
  today: Date = new Date(),
): ScholarshipGraduationYear[] => {
  const firstYear = today.getFullYear();
  return Array.from(
    { length: GRADUATION_YEARS_OFFERED },
    // The template type cannot be inferred from arithmetic, and the window is inside the
    // pattern's century by construction.
    (_, i) => `${firstYear + i}` as ScholarshipGraduationYear,
  );
};

/*
  Reading order for a mixed set: the eligibility ladder in its own order, then JC 1-3, then
  graduation years oldest first. Sorting them together as strings would file 2027 between 1 and
  3. Mirrors ClassSortKey in ScholarshipAllocationCalculator.
*/
export const orderClasses = (classes: Iterable<ScholarshipClass>): ScholarshipClass[] => {
  const present = new Set<string>(classes);
  const ladder = SCHOLARSHIP_CLASSES.filter((c) => present.has(c));
  const juco = SCHOLARSHIP_JUCO_CLASSES.filter((c) => present.has(c));
  const years = [...present].filter(isGraduationYearClass).sort();
  return [...ladder, ...juco, ...years];
};

/** How a class reads to a coach: "JC 2" for a junior college year, anything else as itself. */
export const classDisplayLabel = (c: string): string =>
  isJucoClass(c) ? `JC ${c.slice(2)}` : c;

/*
  The class a row holds on its own terms: its typed value, else the linked player record's
  graduation year. This is what the class editor seeds from, because it is the only part of what
  the board shows that the field can save.

  A row attached from the prospect pool carries no class of its own — the attach writes the
  name, position, bats and throws snapshot and nothing else — and its class comes from the
  linked player record's graduation year. A row typed straight onto the roster has no player
  record and carries the value itself. Reading `player.class` alone therefore misses every
  attached player, which is how an attached prospect could show "2027" in the Class column and
  still vanish from the board the moment a class filter was on.

  The ROW's own value wins, and the graduation year is the fallback. A row only holds a class
  because a coach typed one there, and on a linked row that value is a year of eligibility —
  which a player record cannot hold, so the row is its only home. Reading the graduation year
  first would hide it, which is the same silent no-op that made editing the class on an
  attached player look broken. Mirrors ApplyClassWrite in ScholarshipRepository.

  A grad year outside the pinned century is not read as a class at all: nothing here can vouch
  for what a stray value means.
*/
export const storedClass = (
  player: Pick<ScholarshipPlayer, 'class' | 'gradYear'>,
): ScholarshipStoredClass | null => {
  if (player.class != null) return player.class;
  if (player.gradYear != null) {
    const asText = String(player.gradYear);
    if (isGraduationYearClass(asText)) return asText;
  }
  return null;
};

/*
  The class a player is actually shown under on the board, in the filters and on the summary card.

  The API resolves it once per row (ScholarshipClassResolver): the row's own class, else JC1-JC3
  for a JUCO player with a JC year, else the graduation year. The summary card's class breakdown
  comes from the same resolver, so reading the API's value here is what keeps the board and the
  card filing a player under one class.

  Web and API deploy as separate jobs, so an older API sends no effectiveClass at all. Then this
  falls back to storedClass, which is exactly what every screen showed before JC classes existed.
  A value the API sends that this build cannot read also falls back rather than filing the
  player under something no filter or ordering knows about.
*/
export const effectiveClass = (
  player: Pick<ScholarshipPlayer, 'class' | 'gradYear' | 'effectiveClass'>,
): ScholarshipClass | null => {
  const fromApi = player.effectiveClass;
  if (fromApi === null) return null;
  if (fromApi !== undefined && isScholarshipClass(fromApi)) return fromApi;
  return storedClass(player);
};

/**
 * The class a player is shown under, as a coach reads it ("JC 2", "2027", "3"), or null when
 * there is none. For the places with room for the full value: the Players table and the CSV.
 */
export const effectiveClassLabel = (
  player: Pick<ScholarshipPlayer, 'class' | 'gradYear' | 'effectiveClass'>,
): string | null => {
  const shown = effectiveClass(player);
  return shown == null ? null : classDisplayLabel(shown);
};

/*
  The class as the depth chart's cards show it: a graduation year as its last two digits, a
  JC class as "JC 2", a year of eligibility as itself.

  A card is a narrow column carrying rank, name, class, bats/throws and a money figure on one
  line, and the rows already wrapped once when the DSA badge was added. Two characters back is
  the difference between "2027 · R/R" fitting beside a long name and not. It also reads the way
  a coach says it — the class of '27 — and matches the two-digit values already on those cards.

  Everywhere with room for the full value keeps it: the Players table, the CSV export, the
  filter chips and the class-summary card all show 2027. This is a display shortening for the
  cards alone and never what gets stored.
*/
export const compactClassLabel = (
  player: Pick<ScholarshipPlayer, 'class' | 'gradYear' | 'effectiveClass'>,
): string | null => {
  const shown = effectiveClass(player);
  if (shown == null) return null;
  return isGraduationYearClass(shown) ? shown.slice(2) : classDisplayLabel(shown);
};
